import type { DashboardPeriodState } from '../useDashboardPeriod'
import { ModalShell } from './ModalShell'
import { useProcurementDetail } from './useDrilldown'

const MATRIX_LABEL: Record<string, string> = {
  STOCK: 'Склад',
  CHINA: 'Китай',
  RF: 'РФ',
  MANUFACTURER: 'Производители',
  OTHER: 'Другое',
}

export function ProcurementDetailModal({
  userId,
  period,
  onClose,
}: {
  userId: string
  period: DashboardPeriodState
  onClose: () => void
}) {
  const { data, isLoading, isError } = useProcurementDetail(userId, period)

  return (
    <ModalShell
      title={data?.name ?? 'Закупщик'}
      subtitle={data ? `${data.email} · аналитика за период` : undefined}
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-5">
          {/* «Средняя маржа» убрана 19.09.2026: цифра всегда равнялась
              1 − 1/коэффициент матрицы и о работе закупщика не говорила. */}
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Квот с ценой" value={data.stats.quotesCount} />
            <Stat
              label="Ср. время ответа"
              value={data.stats.avgResponseHours === null ? '—' : `${data.stats.avgResponseHours}ч`}
              hint="от появления позиции в заявке до его квоты"
            />
          </div>

          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">По матрицам</div>
            {data.byMatrix.length === 0 ? (
              <p className="text-sm text-gray-400">Нет данных</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className="text-left font-normal pb-2">Матрица</th>
                    <th className="text-right font-normal pb-2">Квот</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byMatrix.map((m) => (
                    <tr key={m.matrix} className="border-t border-gray-100">
                      <td className="py-2 text-gray-800">{MATRIX_LABEL[m.matrix] ?? m.matrix}</td>
                      <td className="py-2 text-right tabular-nums">{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Топ брендов (по квотам)
              </div>
              {data.topBrands.length === 0 ? (
                <p className="text-sm text-gray-400">Нет данных</p>
              ) : (
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
                  {data.topBrands.map((b) => (
                    <li
                      key={b.brand}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <span className="text-gray-800 truncate">{b.brand}</span>
                      <span className="text-gray-500 tabular-nums shrink-0">{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Поставщики: «отправили → ответили ценой → ушло в заказ» и
              конверсия отправленного в заказ (запрос Сергея 19.09.2026). */}
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Поставщики: отправлено → квота → заказ
            </div>
            {data.topSuppliers.length === 0 ? (
              <p className="text-sm text-gray-400">Нет данных</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className="text-left font-normal pb-2">Поставщик</th>
                      <th className="text-right font-normal pb-2">Отправлено</th>
                      <th className="text-right font-normal pb-2">Проквотировано</th>
                      <th className="text-right font-normal pb-2">В заказ</th>
                      <th className="text-right font-normal pb-2">Конверсия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topSuppliers.map((s) => (
                      <tr key={s.name} className="border-t border-gray-100">
                        <td className="py-2 text-gray-800">{s.name}</td>
                        <td className="py-2 text-right tabular-nums">{s.sent}</td>
                        <td className="py-2 text-right tabular-nums text-gray-700">{s.quoted}</td>
                        <td className="py-2 text-right tabular-nums text-gray-700">{s.ordered}</td>
                        <td className="py-2 text-right tabular-nums text-gray-500">
                          {s.conversionPercent === null ? '—' : `${s.conversionPercent}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data.timeline.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Активность по дням
              </div>
              <div className="flex gap-0.5 h-16 items-end">
                {data.timeline.map((t) => {
                  const max = Math.max(...data.timeline.map((x) => x.quotes))
                  const h = max === 0 ? 2 : Math.max(4, (t.quotes / max) * 60)
                  return (
                    <div
                      key={t.date}
                      title={`${t.date}: ${t.quotes} квот`}
                      className="flex-1 bg-blue-400 rounded-t"
                      style={{ height: h }}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </ModalShell>
  )
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  /** Пояснение под цифрой — чтобы показатель не приходилось расшифровывать. */
  hint?: string
}) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 mt-0.5">{value}</div>
      {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
    </div>
  )
}
